import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectLimitsModalData, updateLimitsModal } from '~/components/account/ducks';
import { NOTIFICATION_TYPE, sendAppBridgeEvent, showNativeNotification, WEB_EVENTS } from '~/components/app-bridge';
import { isNativeNotificationEnabled } from '~/components/app-bridge/app-properties';
import { selectIsNetherlandsRegulatedFranchise, selectIsNewDepositFlow } from '~/components/brand/ducks/selectors';
import {
	BALANCE,
	DEPOSIT,
	LIMITS_REMAINING_AMOUNT_ENDPOINT,
	SPEND,
	SPENDING_LIMITS_NOTIFICATION_ENDPOINT,
	textKeys
} from '~/components/limits/constants';
import { showQueuedNotification } from '~/components/notifications/ducks/actions';
import useTranslations from '~/components/translation/use-translations';
import { requestApi } from '~/services/api-new';
import { LimitResponseType, SpendLimitMetaType } from '~/components/limits/types';
import { NOTIFICATION_ID } from '../spending-limit/utils/constant';

const useCheckLimits = () => {
	const isNlRegulatedFranchise = useSelector(selectIsNetherlandsRegulatedFranchise);
	const { pending } = useSelector(selectLimitsModalData) || {};
	const dispatch = useDispatch();
	const { translations } = useTranslations(textKeys);
	const [loading, setLoading] = useState(false);
	const isNewDepositFlow = useSelector(selectIsNewDepositFlow);

	const check = useCallback(
		async ({ constraint = 0, checkRGLimits = true } = {}) => {
			if (!isNewDepositFlow) {
				return true;
			}

			if (pending) {
				const message = translations.MOBILE_SPENDING_LIMIT_APPROVAL_IN_PROGRESS;

				if (isNativeNotificationEnabled()) {
					showNativeNotification(NOTIFICATION_TYPE.SPENDING_LIMIT, message);
					sendAppBridgeEvent(WEB_EVENTS.LIMIT_MODAL_CLOSED);
				} else {
					dispatch(
						showQueuedNotification({
							message,
							color: 'neutral',
							autoHide: true,
							openInSidebar: true,
							id: NOTIFICATION_ID
						})
					);
				}

				return false;
			}

			setLoading(true);

			try {
				const limitResponse = await requestApi<LimitResponseType>({
					endpoint: LIMITS_REMAINING_AMOUNT_ENDPOINT
				});
				const { responsibleGamingLimitType, PARAM_MAX_OPERATION } = limitResponse;
				const limitArray = [DEPOSIT, BALANCE, SPEND];

				const remainingAmount = Number(PARAM_MAX_OPERATION);
				if (
					responsibleGamingLimitType === SPEND &&
					(remainingAmount < constraint || remainingAmount === 0) &&
					isNlRegulatedFranchise
				) {
					const data = await requestApi<{
						meta: SpendLimitMetaType;
					}>({ endpoint: SPENDING_LIMITS_NOTIFICATION_ENDPOINT });
					dispatch(
						updateLimitsModal(true, {
							...data,
							responsibleGamingLimitType,
							remainingAmount,
							isNlSpendLimitPopup: true
						})
					);
					return false;
				}

				if (checkRGLimits && remainingAmount === 0 && limitArray.includes(responsibleGamingLimitType)) {
					dispatch(updateLimitsModal(true, { responsibleGamingLimitType, remainingAmount }));

					return false;
				}

				return true;
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Limit check Error:', error);
				const message = translations.MOBILE_GENERAL_ERROR;
				if (isNativeNotificationEnabled()) {
					showNativeNotification(NOTIFICATION_TYPE.GENERAL_ERROR, message);
				} else {
					dispatch(
						showQueuedNotification({
							message,
							color: 'red',
							autoHide: true,
							openInSidebar: true,
							id: NOTIFICATION_ID
						})
					);
				}

				dispatch(updateLimitsModal(false));

				return false;
			} finally {
				setLoading(false);
			}
		},
		[
			dispatch,
			isNewDepositFlow,
			isNlRegulatedFranchise,
			pending,
			translations.MOBILE_GENERAL_ERROR,
			translations.MOBILE_SPENDING_LIMIT_APPROVAL_IN_PROGRESS
		]
	);

	return { check, loading };
};

export default useCheckLimits;
